import { useState } from 'react';
import { Modal } from './Modal';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  url: string;
  onShareToMessage?: () => void;
  onShare?: () => void;
}

function ShareModal({
  isOpen,
  onClose,
  title,
  url,
  onShareToMessage,
  onShare,
}: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onShare?.();
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const shareOptions = [
    ...(onShareToMessage
      ? [
          {
            name: 'Send in Message',
            icon: 'ri-message-3-line',
            color: 'text-purple-600',
            action: () => {
              onShareToMessage();
              onShare?.();
              onClose();
            },
          },
        ]
      : []),
    {
      name: 'Copy Link',
      icon: 'ri-link',
      color: 'text-gray-600',
      action: handleCopyLink,
    },
    {
      name: 'Twitter',
      icon: 'ri-twitter-x-line',
      color: 'text-black',
      action: () => {
        const text = `Check out this post: "${title}"`;
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            text,
          )}&url=${encodeURIComponent(url)}`,
          '_blank',
        );
        onShare?.();
      },
    },
    {
      name: 'Facebook',
      icon: 'ri-facebook-line',
      color: 'text-blue-600',
      action: () => {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            url,
          )}`,
          '_blank',
        );
        onShare?.();
      },
    },
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-line',
      color: 'text-blue-700',
      action: () => {
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            url,
          )}&title=${encodeURIComponent(title)}`,
          '_blank',
        );
        onShare?.();
      },
    },
    {
      name: 'WhatsApp',
      icon: 'ri-whatsapp-line',
      color: 'text-green-600',
      action: () => {
        const text = `Check out this post: "${title}" ${url}`;
        window.open(
          `https://wa.me/?text=${encodeURIComponent(text)}`,
          '_blank',
        );
        onShare?.();
      },
    },
    {
      name: 'Telegram',
      icon: 'ri-telegram-line',
      color: 'text-blue-500',
      action: () => {
        const text = `Check out this post: "${title}"`;
        window.open(
          `https://t.me/share/url?url=${encodeURIComponent(
            url,
          )}&text=${encodeURIComponent(text)}`,
          '_blank',
        );
        onShare?.();
      },
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share">
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 mb-1 line-clamp-2">{title}</h3>
        </div>

        <div className="space-y-2">
          {shareOptions.map((option) => (
            <button
              key={option.name}
              onClick={option.action}
              className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left cursor-pointer"
            >
              <div className="w-10 h-10 flex items-center justify-center">
                <i className={`${option.icon} ${option.color} text-xl`}></i>
              </div>
              <span className="font-medium text-gray-900">
                {option.name === 'Copy Link' && copied ? 'Copied!' : option.name}
              </span>
              {option.name === 'Copy Link' && copied && (
                <i className="ri-check-line text-green-500 ml-auto"></i>
              )}
              {option.name === 'Send in Message' && (
                <span className="ml-auto text-xs text-purple-500 font-medium bg-purple-50 px-2 py-0.5 rounded-full">
                  Direct
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
            <i className="ri-link text-gray-400"></i>
            <input
              type="text"
              value={url}
              readOnly
              className="flex-1 bg-transparent text-sm text-gray-600 outline-none"
            />
            <button
              onClick={handleCopyLink}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Export both default and named exports for compatibility
export default ShareModal;
export { ShareModal };
